import { AdornisRouter } from '@adornis/base/client/a-router';
import { ValueEvent } from '@adornis/base/utilTypes';
import { EntityData } from '@adornis/baseql/entities/adornisEntity';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { BehaviorController } from '@adornis/chemistry/controllers/behavior-controller';
import { DataLoadingController } from '@adornis/chemistry/controllers/data-loading-controller';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import '@adornis/chemistry/elements/components/x-accordeon';
import '@adornis/chemistry/elements/components/x-card';
import '@adornis/chemistry/elements/components/x-flex';
import '@adornis/chemistry/elements/components/x-icon'; //material-icon wrapper
import '@adornis/chemistry/elements/components/x-link';
import '@adornis/chemistry/elements/components/x-progress-bar';
import { elevation } from '@adornis/chemistry/elements/theming/design';
import { ElementsDialog } from '@adornis/dialog/x-dialog';
import { askForFile } from '@adornis/file-utils/client/helper-methods';
import '@adornis/forms/x-input';
import '@adornis/popover/x-dropdown-selection';
import '@material/mwc-linear-progress';
import '@material/mwc-slider/slider-range.js';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Decimation,
  Legend,
  LinearScale,
  LineController,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { shorten } from 'client/main-element';
import { colors } from 'client/startup';
import * as csv from 'csvtojson';
import { EventLog } from 'db/EventLog';
import { Feedback } from 'db/Feedback';
import { GoalAgg } from 'db/GoalAgg';
import { MoodleAttempts, RawMoodleAttempts } from 'db/MoodleAttempts';
import { User } from 'db/User';
import { html, nothing } from 'lit-element';
import { customElement } from 'lit/decorators/custom-element.js';
import { state } from 'lit/decorators/state.js';
import { ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { Aggregator } from 'mingo';
import 'mingo/init/system';
import { combineLatestWith, delay, filter, map, scan, switchMap, tap, throttleTime } from 'rxjs';
import { generateOptions } from './chartUtils';
import { FeedbackPop } from './feedback-pop';
import microtip from './microtip.css';
Chart.register(
  LinearScale,
  LineController,
  PointElement,
  CategoryScale,
  zoomPlugin,
  Title,
  Legend,
  Decimation,
  BarController,
  BarElement,
  Tooltip,
);

@customElement('main-dashboard')
export class MainDashboard extends ChemistryLitElement {
  styles() {
    return {
      '.card': { boxShadow: elevation[1], padding: '20px', gap: '20px', borderRadius: '5px' },
    };
  }

  public chartRef: HTMLCanvasElement | null = null; // Reference to the main chart, needed to initialize chartjs
  public chartMain: any | null = null; // Actual chart element
  public QUARTILEBOUNDS = [0, 0.25, 0.5, 0.75, 1.000001];

  @state() public usernameMapping = new LocalStorageBehaviorController(this, 'usernameMapping', [] as string[][]);

  @state() public courseSelected = new ObservableController(this, AdornisRouter.get().queryParams.get('course'));
  @state() public goalSelected = new BehaviorController<{
    _id: string;
    goalTitle: string;
    goalText: string;
    goalLevel: number;
    avgscore: number;
    taskIds: number[];
  } | null>(this, null);

  @state() public courseid = new BehaviorController(this, '109');
  @state() public updating = new BehaviorController(this, false);
  @state() public lastUpdate = new ObservableController(
    this,
    this.courseSelected.observable.pipe(
      filter(Boolean),
      switchMap(course => EventLog.lastUpdate(course)(EventLog.allFields)),
    ),
  );
  public rawAttempts = new DataLoadingController(this, () =>
    MoodleAttempts.subscribeAll<MoodleAttempts>()(MoodleAttempts.allFields),
  );
  public timeAttempts = new DataLoadingController(this, () =>
    RawMoodleAttempts.subscribeAll<RawMoodleAttempts>()(RawMoodleAttempts.allFields),
  );
  public rawGoals = new DataLoadingController(this, () => GoalAgg.subscribeAll<GoalAgg>()(GoalAgg.allFields));

  @state() public taskcount = new ObservableController(
    this,
    this.rawAttempts.observable.pipe(
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)),
      map(raw => {
        const att = raw.map(att => att.toJSON()) as Array<EntityData<MoodleAttempts>>;
        const agg = new Aggregator([
          { $match: { courseId: this.courseSelected.value } },
          {
            $group: {
              _id: '$moodleTaskId',
              attempts: { $push: '$$CURRENT' },
            },
          },
        ]).run(att) as Array<{
          _id: string;
          attempts: [];
        }>;
        return agg.length;
      }),
    ),
  );

  @state() public users = new ObservableController(
    this,
    this.rawAttempts.observable.pipe(
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)),
      map(raw => {
        const att = raw.map(att => att.toJSON()) as Array<EntityData<MoodleAttempts>>;
        const agg = new Aggregator([
          {
            $group: {
              _id: '$userId',
              userName: { $first: '$userName' },
              pseudoRealname: { $first: '$pseudoRealname' },
              avgscore: {
                $avg: {
                  $ifNull: ['$score', 0],
                },
              }, // { $avg: '$score' },
            },
          },
        ]).run(att) as Array<{
          _id: string;
          userName: string;
          pseudoRealname: string;
          avgscore: number;
          orderScore: number | null;
        }>;
        return agg.sort(function (x, y) {
          return Number(x._id) - Number(y._id);
        });
      }),
    ),
  );

  @state() public userBuckets = new ObservableController(
    this,
    this.timeAttempts.observable.pipe(
      combineLatestWith(this.courseSelected.observable, this.goalSelected),
      map(([x, course, goal]) =>
        x.filter(y => y.courseId === course && (goal === null ? true : y.goalIds.includes(Number(goal._id)))),
      ),
      combineLatestWith(this.users.observable),
      map(([raw, allusers]) => {
        const attempts = raw.map(att => att.toJSON() as EntityData<RawMoodleAttempts>);
        const distFilteredUsers = [...new Set(Object.values(raw.map(x => x.userId)))];
        const distAllUsers = [...new Set(Object.values(allusers.map(x => x._id)))];
        const diff = distAllUsers.filter(x => !distFilteredUsers.includes(x));
        // console.log('############### DIFF #################', diff, distFilteredUsers, distAllUsers, allusers);
        const times = [DateTime.now().plus({ days: -14 }), DateTime.now().plus({ days: -7 }), DateTime.now()];
        const timeSets = times.map(t =>
          new Aggregator([
            { $match: { answerDate: { $lt: t.toJSDate() } } },
            {
              $group: {
                _id: '$userId',
                avgscore: {
                  $avg: {
                    $ifNull: ['$score', 0],
                  },
                },
                // avgscore: { $avg: '$score' },
              },
            },
            {
              $bucket: {
                groupBy: '$avgscore',
                boundaries: this.QUARTILEBOUNDS,
                default: 'Other',
              },
            },
            { $match: { _id: { $ne: 'Other' } } }, // This is just to remove the empty fifth bar, all "null" scores have already been set to 0 in the server aggregation pipeline!
          ]).run(attempts),
        );
        return timeSets.map(x => {
          x[0]!.count = Number(x[0]!.count) + diff.length;
          return x;
        });
      }),
    ),
  );

  @state() public goals = new ObservableController(
    this,
    this.rawGoals.observable.pipe(
      filter(Boolean),
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)), // Don't filter by goalSelected here obviously
      map(raw => {
        const attempts = raw.map(att => att.toJSON() as EntityData<GoalAgg>);
        const agg = new Aggregator([
          {
            $group: {
              _id: '$goalId',
              ...['goalTitle', 'goalText', 'goalLevel', 'taskIds', 'courseName', 'courseId', 'courseGroup'].reduce(
                (acc, field) => ({ ...acc, [field]: { $first: '$' + field } }),
                {},
              ),
              avgscore: {
                $avg: {
                  $ifNull: ['$score', 0],
                },
              }, // { $avg: '$score' },
            },
          },
        ])
          .run(attempts)
          .sort((a, b) => a._id - b._id) as Array<{
          _id: string;
          goalTitle: string;
          goalText: string;
          goalLevel: number;
          avgscore: number;
          taskIds: number[];
        }>;
        return agg;
      }),
    ),
  );

  // This is almost 1:1 the same as in the table-view
  @state() public tasks = new ObservableController(
    this,
    this.rawAttempts.observable.pipe(
      filter(Boolean),
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)),
      map(raw => {
        const attempts = raw.map(att => att.toJSON() as EntityData<MoodleAttempts>);
        return new Aggregator([
          {
            $group: {
              _id: '$taskId',
              ...[
                'taskTitle',
                'taskQuestion',
                'taskType',
                'linkAccordeon',
                'courseName',
                'courseId',
                'courseGroup',
              ].reduce((acc, field) => ({ ...acc, [field]: { $first: '$' + field } }), {}),
              avgscore: {
                $avg: {
                  $ifNull: ['$score', 0],
                },
              }, // { $avg: '$score' },
            },
          },
        ])
          .run(attempts)
          .sort((a, b) => a._id - b._id) as Array<{
          _id: string;
          taskTitle: string;
          taskText: string;
          taskLevel: number;
          linkAccordeon: string;
          avgscore: number;
        }>;
      }),
    ),
  );

  @state() public goaltasks = new ObservableController(
    this,
    this.goals.observable.pipe(
      combineLatestWith(this.tasks.observable),
      map(([goals, tasks]) =>
        goals.map(g => ({
          title: html`<style>
              ${microtip}
            </style>
            <x-flex style="width: 170px;">
              <x-text style="white-space: nowrap; width: 170px;"> ${g._id}. ${g.goalTitle} </x-text>
            </x-flex>
            <mwc-linear-progress
              style="min-width:100px; width:100%; --mdc-theme-primary: ${this.colors.accent}; z-index:-1;"
              progress=${g.avgscore}
            ></mwc-linear-progress>
            <x-flex style="width:30px;">
              <x-text style="width:30px; white-space: nowrap;">${(g.avgscore * 100).toFixed() + '%'}</x-text></x-flex
            >`,
          text: html` <x-flex space="md">
            <x-text style="grid-column-start:1; grid-column-end:5; font-style: italic; text-align:center;"
              >${g.goalText}</x-text
            >
            <x-grid style="grid-template-columns: min-content auto min-content min-content; grid-gap:20px;">
              ${g.taskIds
                .sort((a, b) => Number(a) - Number(b))
                .map(
                  taskid => html`
                    <x-text
                      data-microtip-position="bottom"
                      role="tooltip"
                      aria-label="${tasks[tasks.findIndex(t => t._id === String(taskid))]?.taskText}"
                      style="white-space:nowrap;"
                    >
                      Task ${tasks[tasks.findIndex(t => t._id === String(taskid))]?._id}
                    </x-text>
                    <mwc-linear-progress
                      style="width:100%; min-width:50px; justify-self:center; align-self:center; max-width:300px; --mdc-theme-primary: ${this
                        .colors.accent}; z-index:-1;"
                      progress=${tasks[tasks.findIndex(t => t._id === String(taskid))]?.avgscore}
                    ></mwc-linear-progress>
                    <x-text
                      >${((tasks[tasks.findIndex(t => t._id === String(taskid))]?.avgscore || 0) * 100).toFixed() + '%'}
                    </x-text>
                    <x-link href=${tasks[tasks.findIndex(t => t._id === String(taskid))]?.linkAccordeon}>
                      <x-text style="color:blue;">Link</x-text>
                    </x-link>
                  `,
                )}
            </x-grid></x-flex
          >`,
        })),
      ),
    ),
  );

  @state() public fullRecord = new ObservableController(
    this,
    this.rawAttempts.observable.pipe(
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)),
      map(raw => {
        // THIS STUFF COULD AND SHOULD BE DONE AS A MONGO AGGREGATION -- Construct attempts documents from raw data and produce the marginal datasets using Mingo
        const attempts = raw.map(att => att.toJSON() as EntityData<MoodleAttempts>);
        const distusers = new Aggregator([{ $group: { _id: '$userId' } }])
          .run(attempts)
          .map(a => a._id)
          .sort((a, b) => a - b) as Array<{ userId: string }>;
        const disttasks = new Aggregator([{ $group: { _id: '$taskId' } }])
          .run(attempts)
          .map(a => a._id)
          .sort((a, b) => a - b) as Array<{ [key: string]: any }>;
        // (Probably cheaper) alternative:
        // const distusers = [...new Set(goalagg.map(att => att.userId))];
        // const distgoals = [...new Set(goalagg.map(att => att.goalId))];
        // console.log(userIds, taskIds, 'USER & TASK IDS');

        // Construct a two-way hashtable for most efficient memory access
        const tasksEmpty = disttasks.reduce((a, c) => {
          return { ...a, [c]: { score: null, type: null, title: null } };
        }, {}) as Array<Number>;
        const fullrecord = Object.fromEntries(distusers.map(user => [user, JSON.parse(JSON.stringify(tasksEmpty))]));

        // console.log(fullrecord, 'USERRECORD EMPTY');

        attempts.forEach(att => {
          const u = att.userId; // user id
          const t = att['taskId']; // task id
          fullrecord[u]![t]! = att;
        });
        return fullrecord;
      }),
    ),
  );

  @state() public percStart = new BehaviorController<null | number>(this, 0.25);
  @state() public percEnd = new BehaviorController<null | number>(this, 0.75);

  @state() public percUsers = new ObservableController(
    this,
    this.users.observable.pipe(
      combineLatestWith(this.updateComplete, this.percStart, this.percEnd),
      map(([users, update, start, end]) => {
        if (this.percStart === null || !this.percEnd) return null;
        // await 0; // To ensure that everything is run through
        return users.filter(u => u.avgscore >= start! && u.avgscore <= end!);
      }),
    ),
  );

  @state() public mainchartToggle = new BehaviorController(this, true);
  public wewefwef = this.mainchartToggle.subscribe(() => {
    if (this.userBuckets.value) this.updateChart(this.userBuckets.value);
  });

  @state() public updatedstate = new ObservableController(
    this,
    this.userBuckets.observable.pipe(
      combineLatestWith(this.updateComplete),
      delay(1000), // Necessary to wait for the canvas ref() to execute!
      tap(([tasks, update]) => {
        // console.log('update complete and tasks ready! chartmain, chartref:', this.chartMain, this.chartRef);
        try {
          if (!this.chartMain) this.initChartjs();
        } catch (e) {
          console.log('Chart existed already', e);
        }
        this.updateChart(tasks);
      }),
      throttleTime(300),
    ),
  );
  // @state() public realnames = new DataLoadingController(this, () => User.subscribeAll<User>()(User.allFields));

  @state() public updateStream = new ObservableController(this, EventLog.updating()(EventLog.allFields));
  @state() public currentUpdate = new ObservableController(
    this,
    this.updateStream.observable.pipe(
      scan((a, c) => {
        if (c === 'null' || c === 'undefined') return [] as string[];
        a.push(c);
        return a;
      }, [] as string[]),
    ),
  );

  render() {
    // console.log('this.currentUpdate.value', this.lastUpdate.value, this.updateStream.value, this.currentUpdate.value);
    // console.log('RENDER');
    // console.log('GOALS', this.goals.value);
    // console.log('TASKS', this.tasks.value);
    // console.log('GOALTASKS', this.goaltasks.value);
    // console.log('USERS', this.users.value);
    // console.log('USERBUCKETS', this.userBuckets.value);
    // console.log(
    //   'REALNAMES',
    //   this.realnames.value?.map(x => x.toObject()),
    // );
    // console.log('Raw Attempts', this.rawAttempts.value);
    // console.log('Time Attempts', this.timeAttempts.value);
    // console.log('raw goals', this.rawGoals.value);
    // console.log(
    //   'USERBUCKETS',
    //   this.userBuckets.value?.map(x => x.reduce((a, c) => a + c.count, 0)),
    // );
    // console.log('USERS', this.users.value);
    // Build "update chart data function reactively"
    // console.log('ACCORDEON', this.get_accordeon_items());

    if (
      ![
        '377',
        '375',
        '360',
        '364',
        '366',
        '367',
        '368',
        '372',
        '373',
        '339',
        '340',
        '341',
        '342',
        '343',
        '344',
        '345',
        '346',
        '347',
        '348',
        '349',
        '350',
        '351',
        '352',
        '353',
        '354',
        '355',
        '356',
        '357',
        '358',
        '109',
        '103',
      ].includes(this.courseSelected.value!)
    )
      return html`<x-flex style="height:90vh;" center crossaxis-center space="md">
        <x-text>Not a valid course!</x-text>
        <x-text>Please insert your course ID:</x-text>
        <input
          type="text"
          @input="${e => {
            console.log(e.target.value, 'textarea');
            this.courseid.next(e.target.value);
          }}"
          name="name"
          id="uniqueID"
          value=${this.courseid.value}
        />
        <!-- <textarea
          style="white-space: normal"
          @input="${e => {
          console.log(e.target.value, 'textarea');
          this.courseid.next(e.target.value);
        }}"
        >
      ${this.courseid.value} </textarea
        > -->
        <x-button
          style="width:100%;"
          @click="${() => {
            console.log('courseid', this.courseid.value);
            [
              '377',
              '375',
              '360',
              '364',
              '366',
              '367',
              '368',
              '372',
              '373',
              '339',
              '340',
              '341',
              '342',
              '343',
              '344',
              '345',
              '346',
              '347',
              '348',
              '349',
              '350',
              '351',
              '352',
              '353',
              '354',
              '355',
              '356',
              '357',
              '358',
              '109',
              '103',
            ].includes(this.courseid.value!)
              ? AdornisRouter.goTo('/?course=' + this.courseid.value)
              : console.log('dklnadskljadkjladkjldjkl');
          }}"
          >Submit</x-button
        >
        <x-button style="width:100%;" href="/?course=103">Testkurs Laptop</x-button>
        <x-button style="width:100%;" href="/?course=109">Testkurs Solarzellen</x-button>
      </x-flex>`;

    if (!this.goaltasks.value || !this.goals.value || !this.users.value || !this.users.value || !this.userBuckets.value)
      return html`<x-flex style="height:90vh;" center crossaxis-center><x-loader></x-loader></x-flex>`;
    return html`
      <x-flex space="lg" center crossaxis-center>
        <x-flex style="height:50px;"></x-flex>
        <!-- <x-h1 style="text-align:center;"
          >Main Dashboard ${this.rawAttempts.value?.find(x => x.courseId === this.courseSelected.value)?.courseGroup}
          Kurs</x-h1
        > -->

        <!-- <x-button @click="${() => Feedback.sendFeedback()(Feedback.allFields)}">MqSql test</x-button> -->

        <!-- <x-flex horizontal space="md"> 
          <x-text> CourseId: ${this.courseSelected.value} </x-text>
          <x-text>
            CourseName: ${this.rawAttempts.value?.find(x => x.courseId === this.courseSelected.value)?.courseName}
          </x-text>
          <x-text>
            CourseGroup: ${this.rawAttempts.value?.find(x => x.courseId === this.courseSelected.value)?.courseGroup}
          </x-text>
        </x-flex>-->

        <!-- FIRST CARD -->
        <x-flex center crossaxis-center class="card" style="max-width:1000px; max-height:800px; width: 100%; ">
          <x-flex horizontal crossaxis-center>
            <x-h2 style="text-align:center;">Leistungsverteilung aller SuS nach bisheriger Gesamtpunktzahl</x-h2>
            <x-icon
              type="material-outlined"
              style="margin-left:15px;cursor: pointer;"
              @click=${() =>
                ElementsDialog.alert(
                  `Die bisherige Gesamtpunktzahl eines Schülers zeigt an, wie viel % der bisherigen Fragen
                richtig beantwortet worden ist.`,
                )}
            >
              info
            </x-icon>
          </x-flex>
          <!-- <x-text style="padding-bottom:15px;font-size:16px"
            >Auf welchen Leistungsniveaus befinden sich die Schüler*innen?</x-text
          > -->
          <!-- <x-text style="padding-bottom:15px;font-size:16px"
            >b) Wie sind die Leistungen der SuS verteilt?</x-text
          > -->

          <canvas
            style="width:70%;"
            ${ref(x => {
              // console.log('executing canvas ref');
              return (this.chartRef = x as HTMLCanvasElement);
            })}
          ></canvas>
          <x-flex space="md" crossaxis-center style="width:100%; ">
            <x-grid style="grid-template-columns: 3fr 1fr; grid-gap: 20px;">
              <x-flex horizontal space="md">
                <x-dropdown-selection
                  style="width:200px; padding-left:20px;"
                  .value=${'All'}
                  placeholder="Filter nach Lernzielen"
                  @value-changed=${(e: ValueEvent<{ title: string }>) => {
                    this.goalSelected.next(e.detail.value === 'All' ? null : e.detail.value);
                    console.log('CHANGED', e.detail.value);
                  }}
                  .selectables=${[null, ...this.goals.value]}
                  .renderString=${(goal: {
                    _id: string;
                    goalTitle: string;
                    goalText: string;
                    goalLevel: number;
                    avgscore: number;
                    taskIds: number[];
                  }) => {
                    return goal === null ? 'All' : goal.goalTitle;
                  }}
                ></x-dropdown-selection>

                <x-flex>
                  <x-text style="flex:1; padding:0px 20px;">
                    Wählen Sie ein Leistungsintervall, um ein Feedback an alle SuS zu senden, deren Durchschnittsscore
                    in diesem Bereich liegt.
                  </x-text>
                  <x-flex space="md" horizontal space-between crossaxis-center style="padding:0px 20px;">
                    <x-text
                      >Leistungsintervall: ${(this.percStart.value! * 100).toFixed()}% -
                      ${(this.percEnd.value! * 100).toFixed()}%
                    </x-text>
                    <x-text>${this.percUsers.value?.length} von ${this.users.value.length} SuS ausgewählt</x-text>
                  </x-flex></x-flex
                >
              </x-flex>
              <mwc-slider-range
                style="width:100%; --mdc-theme-primary: ${this.colors.accent}; flex:1;"
                id="feedback-slider"
                flex
                discrete
                withTickMarks
                step="0.05"
                min="0"
                max="1"
                valueStart=${this.percStart.value}
                valueEnd=${this.percEnd.value}
                @input=${e => {
                  this.percStart.next(e.target.valueStart);
                  this.percEnd.next(e.target.valueEnd);
                }}
              >
              </mwc-slider-range>
              <x-flex space="sm" style="grid-column: 2; grid-row-start: 1; grid-row-end: 3;">
                <x-button
                  style="height:35px"
                  icon="history"
                  @click=${() => this.mainchartToggle.next(!this.mainchartToggle.value)}
                >
                  ${this.mainchartToggle.value == true ? `Letzen 2 Wochen anzeigen` : `Letzen 2 Wochen verbergen`}
                </x-button>
                <x-button
                  style="align-self:center; width:100%; height:35px"
                  icon="bar_chart"
                  @click="${() => AdornisRouter.goTo('./goal-hist')}"
                >
                  Verteilung nach allen Lernzielen
                </x-button>
                <x-button
                  style="align-self:center; width:100%; height:35px"
                  icon="lightbulb"
                  @click=${() => {
                    FeedbackPop.showPopup({
                      props: {
                        mode: 'percgoal',
                        percStart: this.percStart.value,
                        percEnd: this.percEnd.value,
                        goalIdFilter: this.goalSelected.value?._id,
                        ...Object.fromEntries(
                          Object.entries(this.goalSelected.value || {}).map(([i, v]) => ['popdata' + i, v]),
                        ),
                      },
                    });
                    console.log(
                      'Sending feedback for percentiles',
                      this.shadowRoot?.querySelector('#feedback-slider')?.valueStart,
                      'to',
                      this.shadowRoot?.querySelector('#feedback-slider')?.valueEnd,
                    );
                    // SEND FEEDBACK TO MOODLE API
                  }}
                >
                  Feedback senden
                </x-button>
              </x-flex>
            </x-grid>
          </x-flex>
        </x-flex>

        <!-- SECOND CARD -->
        <x-flex center horizontal space="lg" wrap style="width:55vw; max-width:1200px;">
          <x-flex crossaxis-center class="card" space="lg" flex style="height: fit-content;">
            <x-h2>Durchschnittspunktzahl der Klasse nach Lernzielen</x-h2>
            <x-text>
              Wie viel % der bisherigen Aufgaben in den jeweiligen Lernzielen hat die Klasse richtig
              beantwortet?</x-text
            >
            <x-accordeon style="width:100%;" .items=${Object.values(this.goaltasks.value)}></x-accordeon>
            <x-flex space="lg" horizontal crossaxis-center>
              <x-button style="align-self:center;" icon="flag" @click="${() => AdornisRouter.goTo('./goal-view')}">
                Lernziele
              </x-button>
              <x-button style="align-self:center;" icon="task" @click="${() => AdornisRouter.goTo('./task-view')}">
                Aufgaben
              </x-button>
            </x-flex>
          </x-flex>

          <!-- THIRD CARD -->
          <x-flex crossaxis-center space="lg" class="card" flex style="height: fit-content;">
            ${console.log('this.users.value...')} ${console.log(this.users.value)}
            ${console.log('this.fullrecord.value...')} ${console.log(this.fullRecord.value)}
            <x-h2>Gesamt-Score der SuS</x-h2>
            <x-text>Wie viel % der bisherigen Aufgaben haben die SuS richtig beantwortet? </x-text>
            <x-grid style="grid-template-columns: min-content auto min-content; grid-gap:20px; width:100%;">
              ${this.users.value
                .sort((a, b) => a.avgscore - b.avgscore)
                .map(
                  u => html`
                  <x-text
                    style="align-self:center;"
                    data-microtip-position="top"
                    role="tooltip"
                    data-microtip-size="medium"
                    aria-label="${
                      this.usernameMapping.value?.find(x => x[0] === u.userName) ? shorten(u.userName) : ''
                    }"
                  >
                   ${shorten(this.usernameMapping.value?.find(x => x[0] === u.userName)?.[2] || u.pseudoRealname)}
                  </x-text>
                  <mwc-linear-progress
                    flex
                    style="width:100px; min-width:100px; --mdc-theme-primary: ${
                      this.colors.accent
                      // this.get_color(u.avgscore,'',g)
                    }; z-index:-1; align-self:center; width:100%;"
                    progress=${u.avgscore}
                  >
                  </mwc-linear-progress>
                  <x-flex horizontal space="sm" crossaxis-center>
                    <x-icon
                    @click=${() =>
                      ElementsDialog.alert(html` <x-flex crossaxis-center space="sm">
                        <x-h2>
                          Warnung bei
                          ${this.usernameMapping.value?.find(x => x[0] === u.userName)?.realName || u.pseudoRealname}
                        </x-h2>
                        <x-text style="padding-top:25px"
                          ><b
                            >${this.usernameMapping.value?.find(x => x[0] === u.userName)?.realName ||
                            u.pseudoRealname}</b
                          >
                          hat nach Aufgabe 8 weniger als 20% der Aufgaben richtig gelöst. Dies kann dazu führen, dass
                          ${shorten(
                            this.usernameMapping.value?.find(x => x[0] === u.userName)?.realName || u.pseudoRealname,
                          )}
                          am Ende der Einheit im unteren viertel abschneidet.<br />
                          <b
                            >${Number(
                              Object.values(this.fullRecord.value[u._id])
                                .filter(task => parseInt(task.moodleTaskId) <= 8)
                                .reduce((a, c) => a + c.score, 0),
                            ).toFixed(2)}Punkte</b
                          >
                          bis Aufgabe 8 erreicht.</x-text
                        >
                      </x-flex>`)}
                      style="cursor: pointer; visibility:${
                        this.fullRecord.value[u._id][this.tasks.value?.findIndex(t => t._id === String(8))!] !== null &&
                        (Object.values(this.fullRecord.value[u._id])
                          .filter(task => parseInt(task.moodleTaskId) <= 8)
                          .reduce((a, c) => a + c.score, 0) as number) < 1.5
                          ? 'visible'
                          : 'hidden'
                      }; color:${colors.red}; font-size:15pt;"
                      data-microtip-position="top"
                      role="tooltip"
                      aria-label=
                    >
                      warning
                    </x-icon>
                    <x-text>${(u.avgscore * 100).toFixed() + '%'}</x-text>
                  </x-flex>
                `,
                )}
            </x-grid>
            <x-flex space="lg" horizontal crossaxis-center>
              <x-button style="align-self:center;" icon="flag" @click="${() => AdornisRouter.goTo('./goal-view')}"
                >Lernziele</x-button
              >
              <x-button style="align-self:center;" icon="task" @click="${() => AdornisRouter.goTo('./task-view')}"
                >Aufgaben</x-button
              >
            </x-flex>
          </x-flex>
        </x-flex>

        <!-- UPDATE DATA -->
        <x-flex space="md" style="padding: 20px; width:50%; box-shadow: ${elevation[1]}; border-radius: 5px; ">
          <x-button
            icon="upload_file"
            @click=${async () => {
              const upload: File | undefined = (
                await askForFile({
                  acceptFileTypes: '.csv',
                  acceptMultipleFiles: false,
                })
              )[0];
              if (!upload) return;

              const text = await upload.text();
              const raw: string[][] = await csv
                .default({
                  noheader: false,
                  output: 'csv',
                  delimiter: 'auto',
                })
                .fromString(text);

              // Check for duplicates in the nickname column
              const nicks = raw.map(x => x[0]);
              if (nicks.length !== new Set(nicks).size)
                return ElementsDialog.alert('Duplicate nicknames present, aborting upload!');

              console.log('NICKS', raw);
              this.usernameMapping.next(raw);

              return;
              const mapping = raw.map(
                stud => new User({ _id: stud[0], realName: stud[2], password: stud[1], consent: stud[3] }),
              );
              const updateres = await Promise.all(
                mapping.map(async el => {
                  let tryupdate: number | null = null;
                  try {
                    tryupdate = await el.save();
                    return 1;
                  } catch (e) {
                    console.log('User', el, "didn't exist yet.", tryupdate, e);
                    el.create();
                    return 0;
                  }
                }),
              );
              // const hash = Object.fromEntries(mapping);
              // console.log(mapping);
              console.log(
                'updating users',
                mapping.map(el => el.toJSON()),
              );
              const users = await User.findAll()(User.allFields);
              console.log('New user mapping set', users);
              const freq = updateres.reduce((acc, e) => acc.set(e, (acc.get(e) || 0) + 1), new Map());
              ElementsDialog.alert(`Updated ${freq.get(1) || 0} users, created ${freq.get(0) || 0}`);
            }}
          >
            Upload real name mapping
          </x-button>
          <!-- ?loading=${!['undefined', 'null'].includes(this.updateStream.value || '')} -->
          <x-button
            icon="update"
            @click=${() => {
              console.log(this.courseSelected.value);
              this.updating.next(true);
              MoodleAttempts.updateFromMoodle(this.courseSelected.value)(MoodleAttempts.allFields).then(() =>
                this.updating.next(false),
              );
            }}
          >
            Update data for current course
          </x-button>
          <x-text style="text-align:center;">
            ${['undefined', 'null'].includes(this.updateStream.value || '') || !this.updateStream.value
              ? `Last update for this course: ${this.lastUpdate.value?.toLocaleString(
                  DateTime.DATETIME_SHORT_WITH_SECONDS,
                )}`
              : this.updateStream.value}
          </x-text>
          <x-text style="text-align:center;">
            Last updated:
            ${this.lastUpdate.value
              ? this.lastUpdate.value.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS as string)
              : nothing}
          </x-text>
        </x-flex>
      </x-flex>
    `;
  }

  public initChartjs() {
    if (!this.chartRef) return;
    const context = this.chartRef.getContext('2d')!;
    Chart.defaults.font.size = 16;
    this.chartMain = new Chart(context, {
      type: 'bar',
      data: {
        labels: ['bis vorletzte Woche', 'bis letzte Woche', 'bis Heute'], //
        datasets: [
          {
            label: 'Level 1 (0%-24%)',
            backgroundColor: colors.red,
            data: [],
          },
          {
            label: 'Level 2 (25%-49%)',
            backgroundColor: colors.orange,
            data: [],
          },
          {
            label: 'Level 3 (50%-74%)',
            backgroundColor: colors.yellow,
            data: [],
          },
          {
            label: 'Level 4 (75%-100%)',
            backgroundColor: colors.green,
            data: [],
          },
        ],
      },
      options: generateOptions('', 'Anzahl Schüler*innen', { legend: true }),
    });
    this.chartMain.options.onHover = this.genQuartileHoverHandler();

    console.log('CONTEXT', context);
    console.log('CHART REF', this.chartRef);
    if (!this.chartRef) return;
    this.chartRef.onclick = this.genQuartileFeedbackHandler();
  }

  public updateChart(input: Array<{ [key: number]: { count: number; _id: number } }>) {
    console.log('chart update, input:', input);
    // Feed the data in as a series for every quartile
    if (!this.chartMain || !this.chartMain.data || !this.chartMain.data.datasets![0].data || !this.chartRef) return;

    if (this.mainchartToggle.value) {
      input = [input[2]];
      this.chartMain.data.labels = ['bis Heute'];
    } else {
      this.chartMain.data.labels = ['bis vorletzte Woche', 'bis letzte Woche', 'bis Heute'];
    }
    this.chartRef.onclick = this.genQuartileFeedbackHandler();
    this.chartMain.options.categoryPercentage = this.mainchartToggle.value ? 0.3 : 1;

    const flipped = { 0: {}, 1: {}, 2: {}, 3: {} };
    for (let [gi, group] of Object.entries(input)) {
      for (let [qi, quart] of Object.entries(input[gi])) {
        flipped[qi][gi] = input[gi][qi];
      }
    }

    for (const [qi, quart] of Object.entries(flipped)) {
      this.chartMain.data.datasets![qi].data = Object.values(quart).map(bucket => bucket.count);
    }
    this.chartMain.update();
  }

  public genQuartileFeedbackHandler() {
    return e => {
      if (!this.chartMain) return;
      const points = this.chartMain.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true) as Array<{
        element: Element;
        datasetIndex: number;
        index: number;
      }>;
      console.log('POINTS', points[0]);
      if (!points.length) return;
      const timeGroup = points[0]!.index;
      if (timeGroup < 2 && !this.mainchartToggle.value) return; // Feedback only possible for current week
      const quartBounds = this.QUARTILEBOUNDS;
      const quartile = points[0]!.datasetIndex;
      const percStart = quartBounds[quartile];
      const percEnd = quartBounds[quartile + 1];
      console.log(
        'Init Feedback dialog',
        percStart,
        percEnd,
        this.goalSelected.value,
        Object.fromEntries(Object.entries(this.goalSelected.value || {}).map(([i, v]) => ['popdata' + i, v])),
      );
      FeedbackPop.showPopup({
        props: {
          mode: 'perc',
          percStart: percStart,
          percEnd: percEnd,
          goalIdFilter: this.goalSelected.value?._id,
          ...Object.fromEntries(Object.entries(this.goalSelected.value || {}).map(([i, v]) => ['popdata' + i, v])),
        },
      });
    };
  }
  public genQuartileHoverHandler() {
    return (event, activeElements) => {
      (event?.native.target as HTMLElement).style.cursor =
        activeElements?.length > 0 &&
        ((activeElements[0].index === 2 && !this.mainchartToggle.value) ||
          (activeElements[0].index === 0 && this.mainchartToggle.value))
          ? 'pointer'
          : 'auto';
    };
  }

  // // Goal accordeon
  // private async get_accordeon_items() {
  //   let items: any[] = [];
  //   const mapping = await this.goal2task;
  //   for (const goal in this.goal2task) {
  //     items.push({
  //       title: html`<x-text>
  //         Learning goal ${goal}
  //         <progress value="${this.goal_acc[goal]!}" max="1"></progress>
  //         ${Math.round(this.goal_acc[goal]! * 100)}%
  //       </x-text>`,
  //       text: html`<x-accordeon .items=${this.get_task_items(goal)}></x-accordeon>`,
  //     });
  //   }
  //   return items;
  // }

  // // Task accordeon, nested in every goal accordeon item!
  // private get_task_items(goal: string) {
  //   let items: any[] = [];
  //   for (const i in this.goal2task[goal]) {
  //     const task = this.goal2task[goal]![i]!;
  //     items.push({
  //       title: html`</x-text>Task ${task} <progress value="${this.task_acc[task]!}" max="1"></progress>${Math.round(
  //         this.task_acc[task]! * 100,
  //       )}%</x-text>`,
  //       text: html`
  //         <a
  //           target="_blank"
  //           onclick="window.open(this.href, 'Fenstername', 'width=1000,height=900')"
  //           href="https://lpa.edutec.science/mod/quiz/report.php?id=10189&mode=responses"
  //         >
  //           Versuche überprüfen
  //         </a>
  //       `,
  //     });
  //   }
  //   return items;
  // }
}
